import {getFPTIData} from '../FPTI/FPTIActions';
import { DOM_EVENT, SEND_CLIENTSIDE_FPTI_ACTION } from '../FPTI/FPTIConstants';
import _ from 'lodash';

/**
 * redu middleware for processing client side fpti dispatches
 */
export default () => next => action => {
    //ignore non-fpti actions
    if (action.type !== SEND_CLIENTSIDE_FPTI_ACTION) {
        return next(action);
    }

    //don't log fpti if pa.js isn't loaded
    if (!_.has(window, 'PAYPAL.analytics')) {
        capeLog && capeLog('PAYPAL.analytics unavailable for fpti');
        return next(action);
    }

    //expect a payload object
    if (!action.payload) {
        capeLog && capeLog('payload not provided for fpti');
        return next(action);
    }

    //expect a targetObject object
    if (!action.payload.targetObject) {
        capeLog && capeLog('targetObject was expected for fpti');
        return next(action);
    }

    const { tagName, type } = action.payload.targetObject;

    switch (action.payload.eventType) {
        case DOM_EVENT.CLICK:
            trackButtonAndLinkClick(action);
            break;
        case DOM_EVENT.BLUR:
            if (tagName === 'select') {
                trackDropdownChange(action);
            } else {
                trackTextFieldFocusChange(action);
            }
            break;
        case DOM_EVENT.FOCUS:
            trackTextFieldFocusChange(action);
            break;
        case DOM_EVENT.CHANGE:
            if (type === 'radio') {
                trackRadioChange(action);
            } else {
                trackCheckBoxChange(action);
            }
            break;
    }

    return next(action);
};




/**
 * prepare and send text field focus/blur event to client side fpti
 */
function trackTextFieldFocusChange(action) {
    const targetObject = action.payload.targetObject;
    const fieldName = targetObject.name || targetObject.id;
    const fptiData = getFPTIData(action.payload.pageName);

    fptiData.pgln = `${fptiData.pgrp}|${fieldName}`;
    fptiData.pglk = `${fptiData.pgrp}|${fieldName}`;

    if (targetObject.hasError) {
        fptiData.erfd = fieldName;
        // send error message in erpg property, but only if the user sees it on the screen
        if (targetObject.errorMessageVisible && targetObject.errorMessage) {
            fptiData.erpg = targetObject.errorMessage;
        }
    }

    fptiData.res = {uicomp: {Textfield: {name: fieldName, action: action.payload.eventType}}};
    window.PAYPAL.analytics.logActivity(fptiData);
}

/**
 * prepare and send checkbox field check event to client side fpti
 */
function trackCheckBoxChange(action) {
    const targetObject = action.payload.targetObject;
    const fptiData = getFPTIData(action.payload.pageName);
    const fieldName = targetObject.name || targetObject.id;
    fptiData.pgln = `${fptiData.pgrp}|${fieldName}`;
    fptiData.pglk = `${fptiData.pgrp}|${fieldName}`;

    if (targetObject.hasError) {
        fptiData.erfd = fieldName;
        // send error message in erpg property, but only if the user sees it on the screen
        if (targetObject.errorMessageVisible && targetObject.errorMessage) {
            fptiData.erpg = targetObject.errorMessage;
        }
    }

    //This is the format to be used for customized Dropdowns and checkboxes logged via logActivity api.
    fptiData.res = {uicomp: {Checkbox: {name: fieldName, value: targetObject.value}}};
    window.PAYPAL.analytics.logActivity(fptiData);
}

/**
 * prepare and send radio field update event to client side fpti
 */
function trackRadioChange(action) {
    const targetObject = action.payload.targetObject;
    const fptiData = getFPTIData(action.payload.pageName);
    const fieldName = targetObject.name || targetObject.id;

    fptiData.res = {uicomp: {Radio: {name: fieldName, value: targetObject.value}}};

    if (targetObject.hasError) {
        fptiData.erfd = fieldName;
        // send error message in erpg property, but only if the user sees it on the screen
        if (targetObject.errorMessageVisible && targetObject.errorMessage) {
            fptiData.erpg = targetObject.errorMessage;
        }
    }

    window.PAYPAL.analytics.logActivity(fptiData);
}

/**
 * prepare and send select field update event to client side fpti
 */
function trackDropdownChange(action) {
    const targetObject = action.payload.targetObject;
    const fptiData = getFPTIData(action.payload.pageName);
    const fieldName = targetObject.name || targetObject.id;
    const eventMeta = `${action.payload.eventType}_${((targetObject.value) ? 'option_selected' : 'no_value_selected')}`;

    if (action.payload.targetObject.keypress) {
        // Please note that this keydata is nothing but the number of characters the user has entered in the address field
        fptiData.res = {uicomp: {Dropdown: {name: fieldName, value: targetObject.value, keypress: targetObject.keypress.toString()}}};
    } else {
        //This is the format to be used for customized Dropdowns and checkboxes logged via logActivity api.
        fptiData.res = {uicomp: {Dropdown: {name: fieldName, action: eventMeta}}};
    }
    if (targetObject.hasError) {
        fptiData.erfd = fieldName;
        // send error message in erpg property, but only if the user sees it on the screen
        if (targetObject.errorMessageVisible && targetObject.errorMessage) {
            fptiData.erpg = targetObject.errorMessage;
        }
    }

    window.PAYPAL.analytics.logActivity(fptiData);
}

/**
 * prepare and send button and link click event to client side fpti
 */
function trackButtonAndLinkClick(action) {
    const targetObject = action.payload.targetObject;
    const fptiData = getFPTIData(action.payload.pageName);
    const fieldName = targetObject.name || targetObject.id;
    fptiData.data = {
        pgln: `${fptiData.pgrp}|${fieldName}`,
        pglk: `${fptiData.pgrp}|${fieldName}`,
        link: (fieldName) ? fieldName : targetObject.value,
        uitype: targetObject.role || targetObject.type || targetObject.tagName
    };

    if (targetObject.type === 'submit' || targetObject.type === 'button') {
        const { erfd, erpg } = getErrorFieldsAndMessages();
        fptiData.data.erfd = erfd;
        fptiData.data.erpg = erpg;

        const optOutPhoneMessage = getOptOutPhoneMessage();
        if (optOutPhoneMessage) {
            fptiData.data.res = JSON.stringify(optOutPhoneMessage);
        }
    }

    if (_.has(targetObject, 'href')) {
        fptiData.data.lu = targetObject.href;
    }

    if (_.has(targetObject, 'expanded')) {
        const expandedState = targetObject.expanded ? 'expanded' : 'collapsed';
        fptiData.data.link = `${fptiData.data.link}|${expandedState}`;
    }

    window.PAYPAL.analytics.Analytics.prototype.recordClick(fptiData);
}

export function getErrorFieldsAndMessages() {
    const obj = {};

    const allFormFieldErrors = getAllFormFieldErrors();
    const formBannerMessages = getFormBannerMessages();
    obj.erfd = allFormFieldErrors.errorFields.join('|');
    obj.erpg = [...allFormFieldErrors.errorMsgs, ...formBannerMessages.errorMsgs].join('|');

    return obj;
}

/**
 * Traverse DOM for all fields with data-haserror
 *
 * Assumptions: All fields with this attribute are visible to the user and not hidden, and they have either name or id attributes
 */
export function getAllFormFieldErrors() {
    const fields = Array.prototype.slice.call(document.querySelectorAll('[data-haserror=true]')); // Convert NodeList to Array for IE support
    const errorFields = [];
    const errorMsgs = [];

    fields.forEach(field => {
        const fieldName = field.name || field.id;
        errorFields.push(fieldName);

        if (field.getAttribute('data-errorshowmsg') === 'true') {
            errorMsgs.push(field.getAttribute('data-errormsg'));
        }
    });

    return {
        errorFields,
        errorMsgs
    };
}

/**
 * Get all NotificationBanner messages
 */
export function getFormBannerMessages() {
    const banners = Array.prototype.slice.call(document.querySelectorAll('.NotificationBanner')); // Convert NodeList to Array for IE support
    const errorMsgs = [];

    banners.forEach(banner => {
        errorMsgs.push(banner.textContent);
    });

    return {
        errorMsgs
    };
}

export function getOptOutPhoneMessage() {
    const message = document.querySelector('label[for=phoneOptOut]');

    return message ? {
        uicomp: {
            label: {
                name: 'phoneOptOut',
                value: message.textContent
            }
        }
    } : null;
}
